import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'
import {
  getCompanyExternalApiKeyList,
  patchCompanyExternalApiKey,
  postCompanyExternalApiKey,
  deleteCompanyExternalApiKey,
} from '../../apis/setting'
import { setErrors } from '../common/errorsSlice'
import {
  CompanyExternalApiKey,
  CompanyExternalApiKeyStateInterface,
  deleteCompanyExternalApiKeyRequest,
  deleteCompanyExternalApiKeyResponse,
  patchCompanyExternalApiKeyRequest,
  patchCompanyExternalApiKeyResponse,
  postCompanyExternalApiKeyRequest,
  postCompanyExternalApiKeyResponse,
} from '../../types/setting'
import { sortBy } from 'lodash'
import { sortType } from '../../types/common'
export interface CompanyExternalApiKeyState extends CompanyExternalApiKeyStateInterface {
  isLoading: boolean
}

const initialState: Partial<CompanyExternalApiKeyState> = {
  isLoading: true,
  companyExternalApiKeyList: [],
  isDisabledExternalApi: false,
  sortType: { key: 'created_at', type: 'desc' },
}

const companyExternalApiKeySlice = createSlice({
  name: 'companyExternalApiKey',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    getCompanyExternalApiKeyListSuccess: (state, action: PayloadAction<CompanyExternalApiKey[]>) => {
      state.companyExternalApiKeyList = action.payload
      state.isLoading = false
      return state
    },
    postCompanyExternalApiKeySuccess: (state, action: PayloadAction<postCompanyExternalApiKeyResponse>) => {
      state.isLoading = false
      return state
    },
    patchCompanyExternalApiKeySuccess: (state, action: PayloadAction<patchCompanyExternalApiKeyResponse>) => {
      state.isLoading = false
      return state
    },

    /**
     * 外部APIキー削除成功時のアクション
     *
     * このアクションは、外部APIキーの削除が成功した際にディスパッチされ、
     * 削除されたAPIキーをステートから除外します。
     * また、読み込み状態を完了（false）に設定します。
     *
     * @param {Object} state - 現在のReduxステート
     * @param {PayloadAction<deleteCompanyExternalApiKeyRequest>} action - 削除するAPIキーのID情報を含むアクション
     * @returns 更新されたステート
     *
     * @example
     * // APIキー削除成功時の処理
     * deleteCompanyExternalApiKey(params)
     *   .then((result) => {
     *     dispatch(deleteCompanyExternalApiKeySuccess(params))
     *   })
     */
    deleteCompanyExternalApiKeySuccess: (state, action: PayloadAction<deleteCompanyExternalApiKeyRequest>) => {
      state.companyExternalApiKeyList = state.companyExternalApiKeyList?.filter((item) => item.id !== action.payload.id)
      state.isLoading = false
      return state
    },

    /**
     * 外部APIキーを更新または追加するアクション
     *
     * このアクションは、外部APIキーリストに対して「アップサート」操作を行います。
     * 指定されたIDのAPIキーが既に存在する場合は更新し、存在しない場合は新規追加します。
     *
     * @param {Object} state - 現在のReduxステート
     * @param {PayloadAction<CompanyExternalApiKey>} action - 更新または追加するAPIキー情報を含むアクション
     * @returns 更新されたステート
     *
     * @example
     * // APIキーを更新または追加する
     * dispatch(upsertItemInCompanyExternalApiKeyList(apiKeyData))
     *
     * // レスポンスハンドラー内での使用例
     * .then((result) => {
     *   dispatch(upsertItemInCompanyExternalApiKeyList(result))
     * })
     */
    upsertItemInCompanyExternalApiKeyList: (state, action: PayloadAction<CompanyExternalApiKey>) => {
      const targetItemIndex = state.companyExternalApiKeyList?.findIndex((item) => item.id === action.payload.id) ?? -1
      if (targetItemIndex >= 0 ) {
        state.companyExternalApiKeyList = state.companyExternalApiKeyList || []
        state.companyExternalApiKeyList[targetItemIndex] = action.payload
      } else {
        state.companyExternalApiKeyList = state.companyExternalApiKeyList || []
        state.companyExternalApiKeyList.push(action.payload)
      }
      return state
    },

    /**
     * 外部API機能を無効化するアクション
     *
     * このアクションは、外部API機能を無効にするためのステート更新を行います。
     * プランの制限や権限の問題により外部API機能が利用できない場合や、
     * エラーが発生した場合に呼び出されます。
     *
     * @returns Redux アクション
     *
     * @example
     * // 外部API機能を無効化する
     * dispatch(disableExternalApi())
     *
     * // エラーハンドラー内での使用例
     * .catch((error) => {
     *   if (error.status === 403) {
     *     dispatch(disableExternalApi())
     *   }
     * })
     */
    disableExternalApi: (state) => {
      state.isDisabledExternalApi = true
      return state
    },

    /**
     * 外部API機能を有効化するアクション
     *
     * このアクションは、外部API機能を有効にするためのステート更新を行います。
     *
     * @returns Redux アクション
     *
     * @example
     * // 外部API機能を有効化する
     * dispatch(enableExternalApi())
     */
    enableExternalApi: (state) => {
      state.isDisabledExternalApi = false
      return state
    },

    /**
     * ソートタイプを設定するアクション
     *
     * このアクションは、ソートタイプを設定するためのステート更新を行います。
     * 指定されたキーとタイプに基づいてソートされたリストを生成します。
     *
     * @param {Object} state - 現在のReduxステート
     * @param {PayloadAction<{ key: string, type: 'desc' | 'asc' }>} action - ソートタイプを含むアクション
     * @returns 更新されたステート
     *
     * @example
     * // ソートタイプを設定する
     * dispatch(setSortType({ key: 'created_at', type: 'desc' }))
     */
    setSortType: (state, action: PayloadAction<sortType>) => {
      state.sortType = action.payload
      return state
    },

    /**
     * 外部APIキーリストをソートするアクション
     *
     * このアクションは、外部APIキーリストをソートするためのステート更新を行います。
     * ソートタイプが設定されている場合、指定されたキーとタイプに基づいてソートされたリストを生成します。
     *
     * @param {Object} state - 現在のReduxステート
     * @returns 更新されたステート
     *
     * @example
     * // 外部APIキーリストをソートする
     * dispatch(sortCompanyExternalApiKeyList())
     */
    sortCompanyExternalApiKeyList: (state) => {
      if(state.sortType) {
        state.companyExternalApiKeyList = sortBy(state.companyExternalApiKeyList, state.sortType.key, )

        if(state.sortType.type === 'desc') {
          state.companyExternalApiKeyList = state.companyExternalApiKeyList.reverse()
        }
      }
      return state
    },

    requestFailure: (state) => {
      state.isLoading = false
      return state
    },
  },
})

export const {
  requestStart,
  getCompanyExternalApiKeyListSuccess,
  postCompanyExternalApiKeySuccess,
  patchCompanyExternalApiKeySuccess,
  deleteCompanyExternalApiKeySuccess,
  upsertItemInCompanyExternalApiKeyList,
  disableExternalApi,
  enableExternalApi,
  setSortType,
  sortCompanyExternalApiKeyList,
  requestFailure,
} = companyExternalApiKeySlice.actions

export const companyExternalApiKeyReducer = companyExternalApiKeySlice.reducer

/**
 * APIキー一覧取得用の非同期処理
 *
 * APIから会社に関連付けられたすべての外部APIキーのリストを取得します。
 * 取得に成功すると、APIキーリストを更新し、API機能を有効化します。
 * エラーが発生した場合、特に403エラーの場合はAPI機能を無効化します。
 *
 * @param {void} - パラメータは不要
 *
 * @returns {AppThunk} - Redux Thunk関数
 *
 * @example
 * // コンポーネント内でAPIキー一覧を取得
 * useEffect(() => {
 *   dispatch(fetchCompanyExternalApiKeyList())
 * }, [dispatch])
 *
 * @throws プランが定義されていない場合は外部API機能が無効化されます
 */
export const fetchCompanyExternalApiKeyList = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getCompanyExternalApiKeyList()
    .then((result: CompanyExternalApiKey[]) => {
      dispatch(getCompanyExternalApiKeyListSuccess(result))
      dispatch(sortCompanyExternalApiKeyList())
      dispatch(enableExternalApi())
    })
    .catch((error) => {
      if (error.status === 403) { // FIXME: 本来は403の中でもエラーコードなどにより状況を特定した上で設定したいが、現状ApiClientで丸められてしまうため別対応
        // プランが定義されていないエラーの場合
        dispatch(disableExternalApi())
      }
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

/**
 * 新しいAPIキーを作成する非同期処理
 *
 * 新しい外部APIキーをAPIに登録し、作成に成功するとAPIキーリストを更新します。
 * エラーが発生した場合、特に403エラーの場合はAPI機能を無効化します。
 *
 * @param {postCompanyExternalApiKeyRequest} params - 作成するAPIキーの情報（ラベル名のみ）
 * @param {Function} [callback] - 成功時に呼び出されるコールバック関数
 * @returns {AppThunk} - Redux Thunk関数
 *
 * @example
 * // コンポーネント内でAPIキーを作成
 * dispatch(
 *   createCompanyExternalApiKey(
 *     { label: 'テスト用APIキー' },
 *     (response) => {
 *       closeModal()
 *     }
 *   )
 * )
 *
 * @throws プランが定義されていない場合は外部API機能が無効化されます
 */
export const createCompanyExternalApiKey = (
  params: postCompanyExternalApiKeyRequest,
  callback?: (response: postCompanyExternalApiKeyResponse) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  postCompanyExternalApiKey(params)
    .then((result: postCompanyExternalApiKeyResponse) => {
      dispatch(upsertItemInCompanyExternalApiKeyList(result))
      dispatch(sortCompanyExternalApiKeyList())
      dispatch(postCompanyExternalApiKeySuccess(result))
      callback && callback(result)
    }).catch((error) => {
      if (error.status === 403) { // FIXME: 本来は403の中でもエラーコードなどにより状況を特定した上で設定したいが、現状ApiClientで丸められてしまうため別対応
        // プランが定義されていないエラーの場合
        dispatch(disableExternalApi())
      }
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

/**
 * 既存のAPIキーを更新する非同期処理
 *
 * 既存の外部APIキーをAPIに更新し、更新に成功するとAPIキーリストを更新します。
 * エラーが発生した場合、特に403エラーの場合はAPI機能を無効化します。
 *
 * @param {patchCompanyExternalApiKeyRequest} params - 更新するAPIキーの情報（ID + ラベル名のみ）
 * @param {Function} [callback] - 成功時に呼び出されるコールバック関数
 * @returns {AppThunk} - Redux Thunk関数
 *
 * @example
 * // コンポーネント内でAPIキーを更新
 * dispatch(
 *   updateCompanyExternalApiKey(
 *     { id: 1, label: '更新されたAPIキー' },
 *     (response) => {
 *       closeModal()
 *     }
 *   )
 * )
 *
 * @throws プランが定義されていない場合は外部API機能が無効化されます
 */
export const updateCompanyExternalApiKey = (
  params: patchCompanyExternalApiKeyRequest,
  callback?: (response: patchCompanyExternalApiKeyResponse) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchCompanyExternalApiKey(params)
    .then((result: patchCompanyExternalApiKeyResponse) => {
      dispatch(upsertItemInCompanyExternalApiKeyList(result))
      dispatch(sortCompanyExternalApiKeyList())
      dispatch(patchCompanyExternalApiKeySuccess(result))
      callback && callback(result)
    }).catch((error) => {
      if (error.status === 403) { // FIXME: 本来は403の中でもエラーコードなどにより状況を特定した上で設定したいが、現状ApiClientで丸められてしまうため別対応
        // プランが定義されていないエラーの場合
        dispatch(disableExternalApi())
      }
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

/**
 * 既存のAPIキーを削除する非同期処理
 *
 * 既存の外部APIキーをAPIに削除し、削除に成功するとAPIキーリストを更新します。
 * エラーが発生した場合、特に403エラーの場合はAPI機能を無効化します。
 *
 * @param {deleteCompanyExternalApiKeyRequest} params - 削除するAPIキーの情報（idのみ）
 * @param {Function} [callback] - 成功時に呼び出されるコールバック関数
 * @returns {AppThunk} - Redux Thunk関数
 *
 * @example
 * // コンポーネント内でAPIキーを削除
 * dispatch(
 *   deleteCompanyExternalApiKey(
 *     { id: 1 },
 *     (response) => {
 *       closeModal()
 *     }
 *   )
 * )
 *
 * @throws プランが定義されていない場合は外部API機能が無効化されます
 */
export const destroyCompanyExternalApiKey = (
  params: deleteCompanyExternalApiKeyRequest,
  callback?: (response: deleteCompanyExternalApiKeyResponse) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  deleteCompanyExternalApiKey(params)
    .then((result: deleteCompanyExternalApiKeyResponse) => {
      dispatch(deleteCompanyExternalApiKeySuccess(params))
      dispatch(sortCompanyExternalApiKeyList())
      callback && callback(result)
    }).catch((error) => {
      if (error.status === 403) { // FIXME: 本来は403の中でもエラーコードなどにより状況を特定した上で設定したいが、現状ApiClientで丸められてしまうため別対応
        // プランが定義されていないエラーの場合
        dispatch(disableExternalApi())
      }
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

